import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { chatService } from '../../services/supabaseService';
import { profileService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { MessageCircle, Send } from 'lucide-react';

const DashboardChatsPage = () => {
  const { user, userProfile } = useAuth();
  const isPremium = ['premium', 'active', 'trialing'].includes(
    String(userProfile?.subscriptionStatus ?? userProfile?.subscription_status ?? 'free').toLowerCase()
  );
  const isProvider = userProfile?.role === 'service_provider';
  const canChat = isPremium || isProvider;
  const [searchParams, setSearchParams] = useSearchParams();
  const withUserId = searchParams.get('with');
  const [conversations, setConversations] = useState([]);
  const [selectedConvoId, setSelectedConvoId] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!user?.id) return;
    chatService.listConversations(user.id).then((list) => {
      setConversations(list);
      setLoading(false);
    });
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !withUserId) {
      if (!withUserId) setOtherProfile(null);
      return;
    }
    let convoId = null;
    chatService
      .getOrCreateConversation(user.id, withUserId)
      .then((id) => {
        convoId = id;
        setSelectedConvoId(id);
        return profileService.getProfile(withUserId);
      })
      .then((profile) => {
        setOtherProfile(profile);
        setSearchParams({ with: withUserId }, { replace: true });
      })
      .catch(() => setLoading(false));
    return () => { convoId = null; };
  }, [user?.id, withUserId, setSearchParams]);

  useEffect(() => {
    if (!selectedConvoId) {
      setMessages([]);
      return;
    }
    chatService.listMessages(selectedConvoId).then(setMessages);

    if (!supabase) return;
    const channel = supabase
      .channel(`messages:${selectedConvoId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${selectedConvoId}` },
        () => {
          chatService.listMessages(selectedConvoId).then(setMessages);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConvoId]);

  const selectConversation = (convo) => {
    setSelectedConvoId(convo.id);
    setOtherProfile(convo.other_profile || { id: convo.other_id, display_name: 'Friend' });
    setSearchParams(convo.other_id ? { with: convo.other_id } : {});
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !selectedConvoId || !otherProfile?.id || !user?.id || sending) return;
    setSending(true);
    setInput('');
    try {
      await chatService.sendMessage(selectedConvoId, user.id, otherProfile.id, text);
      const updated = await chatService.listMessages(selectedConvoId);
      setMessages(updated);
    } catch (err) {
      console.error(err);
      setInput(text);
    }
    setSending(false);
  };

  if (!canChat) {
    return (
      <div>
        <h1 className="font-heading font-bold text-2xl text-white mb-4">My Chats</h1>
        <div className="p-12 rounded-2xl bg-slate-800/50 border border-white/10 text-center">
          <MessageCircle className="h-12 w-12 mx-auto mb-4 text-slate-400" />
          <p className="text-slate-300 mb-4">Channels and messaging require an active subscription.</p>
          <Link
            to="/payment"
            className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600"
          >
            Upgrade to chat
          </Link>
        </div>
      </div>
    );
  }

  if (loading && conversations.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-pink border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] min-h-[400px]">
      <h1 className="font-heading font-bold text-2xl text-white mb-4">My Chats</h1>
      <div className="flex-1 flex min-h-0 rounded-2xl border border-white/10 bg-slate-800/50 overflow-hidden">
        <div className="w-72 border-r border-white/10 flex flex-col overflow-hidden">
          <div className="p-2 border-b border-white/10 overflow-y-auto flex-1">
            {conversations.length === 0 ? (
              <p className="p-4 text-slate-400 text-sm">No chats yet. Message someone from Find a Friend.</p>
            ) : (
              conversations.map((convo) => (
                <button
                  key={convo.id}
                  type="button"
                  onClick={() => selectConversation(convo)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                    selectedConvoId === convo.id ? 'bg-brand-pink/20 text-brand-pink' : 'hover:bg-white/10 text-slate-200'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-slate-700 shrink-0 overflow-hidden">
                    <img
                      src={convo.other_profile?.avatar_url || '/default-avatar.svg'}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-white truncate">
                      {convo.other_profile?.display_name || convo.other_profile?.username || 'Friend'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {convo.last_message_at
                        ? new Date(convo.last_message_at).toLocaleDateString()
                        : 'No messages'}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
        <div className="flex-1 flex flex-col min-w-0">
          {selectedConvoId && otherProfile ? (
            <>
              <div className="p-3 border-b border-white/10 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-700 overflow-hidden">
                  <img
                    src={otherProfile.avatar_url || '/default-avatar.svg'}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="font-semibold text-white">
                  {otherProfile.display_name || otherProfile.username || 'Friend'}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((msg) => {
                  const isMe = msg.sender_id === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                          isMe ? 'bg-brand-pink text-white rounded-br-md' : 'bg-slate-700 text-slate-200 rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message_text}</p>
                        <p className={`text-xs mt-1 ${isMe ? 'text-white/80' : 'text-slate-500'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                className="p-3 border-t border-white/10 flex gap-2"
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 rounded-xl border border-white/20 bg-slate-800 text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-brand-pink/30 focus:border-brand-pink"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="p-2 rounded-xl bg-brand-pink text-white hover:bg-brand-pink-dark disabled:opacity-50"
                  aria-label="Send"
                >
                  <Send className="h-5 w-5" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 text-slate-500" />
                <p>Select a chat or message someone from Find a Friend.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardChatsPage;
